import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

// Utils
import { getUserToken } from '~/utils/auth';

export const ADMIN_BATCH_PROGRESS_EVENT = 'admin-batch-progress';
export const ADMIN_BATCH_STATUS_CHANGED_EVENT = 'admin-batch-status-changed';

const DISCONNECT_DELAY_MS = 1000;

// One socket shared by everything that listens for batch events; it closes once nothing uses it
let socketInstance = null;
let subscribers = 0;
let disconnectTimer = null;

const acquireSocket = () => {
  if (disconnectTimer) {
    window.clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }

  subscribers += 1;

  if (!socketInstance) {
    socketInstance = io(import.meta.env.VITE_APP_BASE_URL, {
      transports: ['websocket'],
      // A function, so every (re)connect sends the current token: the API rotates it while the page is open
      auth: (callback) => callback({ token: getUserToken() }),
    });
  }

  return socketInstance;
};

const releaseSocket = (socket) => {
  subscribers = Math.max(0, subscribers - 1);
  if (subscribers > 0 || socket !== socketInstance) return;

  // Deferred so a quick unmount/mount (route change, StrictMode) keeps the connection
  disconnectTimer = window.setTimeout(() => {
    if (subscribers > 0 || socketInstance !== socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    socketInstance = null;
    disconnectTimer = null;
  }, DISCONNECT_DELAY_MS);
};

/**
 * Live batch events for the admin Batches page (system admins only; the server ignores everyone else).
 *
 * - `onProgress(snapshot)`: a batch moved forward in olc-batching-server (throttled by the server)
 * - `onStatusChanged({ batchIds })`: a batch changed status, refetch it
 * - `onReconnect()`: the socket came back after a drop, events in between were missed
 *
 * Handlers may change on every render; the latest ones are always used.
 *
 * @returns {{ connected: boolean }}
 */
const useAdminBatchSocket = ({ onProgress, onStatusChanged, onReconnect } = {}) => {
  const handlersRef = useRef({});
  handlersRef.current = { onProgress, onStatusChanged, onReconnect };

  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const socket = acquireSocket();
    let hasConnected = socket.connected;

    const handleConnect = () => {
      setConnected(true);
      if (hasConnected) handlersRef.current.onReconnect?.();
      hasConnected = true;
    };
    const handleDisconnect = () => setConnected(false);
    const handleProgress = (snapshot) => handlersRef.current.onProgress?.(snapshot);
    const handleStatusChanged = (payload) => handlersRef.current.onStatusChanged?.(payload);

    setConnected(socket.connected);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on(ADMIN_BATCH_PROGRESS_EVENT, handleProgress);
    socket.on(ADMIN_BATCH_STATUS_CHANGED_EVENT, handleStatusChanged);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off(ADMIN_BATCH_PROGRESS_EVENT, handleProgress);
      socket.off(ADMIN_BATCH_STATUS_CHANGED_EVENT, handleStatusChanged);
      releaseSocket(socket);
    };
  }, []);

  return { connected };
};

export default useAdminBatchSocket;
